import { useEffect, useRef, useState } from 'react';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import BlockCard from './BlockCard';
import BlockTypePicker from './BlockTypePicker';
import ContainerCard from './ContainerCard';
import { mergeBlocks, nextPosition, computeMove, BLOCK_KIND_STATIC, BLOCK_KIND_CONTAINER, containerIdOf } from './blockList';
import { getBlockType } from '../../variables/exerciseBlockTypes';
import * as api from './api';
import { buildElementPayload, buildInteractiveElementPayload, buildContainerPayload } from './exerciseTemplatePayload';
import { makeDndId, TOP_LEVEL_END_ID } from '../../components/dnd/dndId';
import { applyDragPreview, buildDndIndex, findEntryInScopes, resolveOver } from '../../components/dnd/containerDragPreview';
import { createContainerCollisionDetection } from '../../components/dnd/collisionDetection';

// Drag-and-drop: any block (static or interactive), wherever it currently
// lives (top-level or inside any container), can be dragged into any
// container or back out to top-level. Containers themselves stay
// non-draggable (they never nest) but still occupy their slot in this
// top-level SortableContext so neighbor-reflow math around them stays
// correct. Canonical `elements`/`interactiveElements`/`containers` (props,
// owned by useExerciseTemplateBuilder.js) are NEVER mutated during a drag -
// `dragPreview` is a pure render-time overlay (see containerDragPreview.js),
// so "drag out without dropping" needs zero rollback bookkeeping, just
// clearing that one piece of state. This is the exact same shape as
// courses/TopicElementList.jsx - see that file's comments for the
// reasoning behind every dnd-kit choice here - generalized from a 2-way
// block/container kind dispatch to a 3-way static/interactive/container one.
export default function BlockList({ exerciseId, courseId, elements, interactiveElements, containers, onReload }) {
   const [pickerOpen, setPickerOpen] = useState(false);
   const [dragPreview, setDragPreview] = useState(null);
   const [awaitingCommit, setAwaitingCommit] = useState(false);
   const lastOverIdRef = useRef(null);
   const collisionDetectionRef = useRef(createContainerCollisionDetection(lastOverIdRef));
   const latestOverRef = useRef(null);
   const dragOverDebounceRef = useRef(null);
   const originContainerRef = useRef(null);
   const hasLeftOriginRef = useRef(false);
   const returnToOriginDebounceRef = useRef(null);
   const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

   // Clears the drag preview only once the canonical data it predicted has
   // actually landed - see TopicElementList.jsx's matching effect for why.
   // Keyed on the PROPS' identities (elements/interactiveElements/
   // containers are owned by useExerciseTemplateBuilder.js, not local state
   // here), unlike the Topic side's own local-state equivalent.
   useEffect(() => {
      if (awaitingCommit) {
         setDragPreview(null);
         setAwaitingCommit(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [elements, interactiveElements, containers]);

   const mergedList = mergeBlocks(elements, interactiveElements, containers);
   const topLevelEntries = mergedList.map((entry) => ({ ...entry, dndId: makeDndId(entry.kind, entry.block.id) }));

   const allScopes = [{ containerId: null, entries: topLevelEntries }];
   containers.forEach((container) => {
      const children = mergeBlocks(elements, interactiveElements, [], container.id)
         .map((entry) => ({ ...entry, dndId: makeDndId(entry.kind, entry.block.id) }));
      allScopes.push({ containerId: container.id, entries: children });
   });
   const dndIndex = buildDndIndex(allScopes);

   const renderedTopLevel = applyDragPreview(topLevelEntries, dragPreview, null);

   const handleAddBlock = (blockType) => {
      setPickerOpen(false);
      const position = nextPosition(mergedList);
      const draft = { type: blockType.type, config: JSON.stringify(blockType.defaultConfig), position };
      const create = blockType.kind === BLOCK_KIND_STATIC
         ? api.createElement(buildElementPayload(exerciseId, draft))
         : api.createInteractiveElement(buildInteractiveElementPayload(exerciseId, draft));
      create.then(() => onReload()).catch(err => console.error(err));
   };

   const handleAddContainer = (layoutType) => {
      setPickerOpen(false);
      const position = nextPosition(mergedList);
      const draft = { layout: layoutType.key, config: JSON.stringify(layoutType.defaultConfig), position };
      api.createContainer(buildContainerPayload(exerciseId, draft))
         .then(() => onReload())
         .catch(err => console.error(err));
   };

   const handleMove = (index, direction) => {
      const swap = computeMove(mergedList, index, direction);
      if (!swap) return;
      const saves = swap.map(entry => {
         if (entry.kind === BLOCK_KIND_CONTAINER) {
            return api.updateContainer(entry.block.id, buildContainerPayload(exerciseId, entry.block));
         }
         const payload = entry.kind === BLOCK_KIND_STATIC
            ? buildElementPayload(exerciseId, entry.block)
            : buildInteractiveElementPayload(exerciseId, entry.block);
         return entry.kind === BLOCK_KIND_STATIC
            ? api.updateElement(entry.block.id, payload)
            : api.updateInteractiveElement(entry.block.id, payload);
      });
      Promise.all(saves).then(() => onReload()).catch(err => console.error(err));
   };

   const handleDragStart = (event) => {
      const activeId = event.active.id;
      const activeEntry = findEntryInScopes(allScopes, activeId);
      if (!activeEntry) return;
      const originContainerId = activeEntry.kind === BLOCK_KIND_CONTAINER ? null : containerIdOf(activeEntry.block);
      const originScope = allScopes.find((scope) => scope.containerId === originContainerId);
      const originIndex = originScope ? originScope.entries.findIndex((entry) => entry.dndId === activeId) : 0;
      originContainerRef.current = originContainerId;
      hasLeftOriginRef.current = false;
      latestOverRef.current = { containerId: originContainerId, index: originIndex };
      setDragPreview({ activeId, activeEntry, overContainerId: originContainerId, overIndex: originIndex });
   };

   const commitResolved = (active, resolved) => {
      latestOverRef.current = resolved;
      clearTimeout(dragOverDebounceRef.current);
      dragOverDebounceRef.current = setTimeout(() => {
         setDragPreview((prev) => (prev && prev.activeId === active.id ? { ...prev, overContainerId: resolved.containerId, overIndex: resolved.index } : prev));
      }, 120);
   };

   const handleDragOver = (event) => {
      const { active, over } = event;
      if (!over) return;
      const resolved = resolveOver(over.id, dndIndex, active.id, allScopes);
      if (!resolved) return;

      const isOrigin = resolved.containerId === originContainerRef.current;
      if (!isOrigin) {
         hasLeftOriginRef.current = true;
         clearTimeout(returnToOriginDebounceRef.current);
         commitResolved(active, resolved);
         return;
      }
      if (!hasLeftOriginRef.current) {
         commitResolved(active, resolved);
         return;
      }
      // See TopicElementList.jsx's matching hysteresis comment - a "back to
      // origin" resolution after having left is only trusted if it's still
      // the answer a short beat later, not applied on the first sighting.
      clearTimeout(returnToOriginDebounceRef.current);
      returnToOriginDebounceRef.current = setTimeout(() => commitResolved(active, resolved), 150);
   };

   const handleDragEnd = () => {
      clearTimeout(dragOverDebounceRef.current);
      clearTimeout(returnToOriginDebounceRef.current);
      if (!dragPreview || !dragPreview.activeEntry) { setDragPreview(null); return; }
      const { activeId, activeEntry } = dragPreview;
      const finalOver = latestOverRef.current || { containerId: dragPreview.overContainerId, index: dragPreview.overIndex };
      const finalPreview = { activeId, activeEntry, overContainerId: finalOver.containerId, overIndex: finalOver.index };

      const targetScope = allScopes.find((scope) => scope.containerId === finalPreview.overContainerId);
      if (!targetScope) { setDragPreview(null); return; }

      const finalOrder = applyDragPreview(targetScope.entries, finalPreview, finalPreview.overContainerId);
      const unchanged = finalOrder.length === targetScope.entries.length
         && finalOrder.every((entry, i) => entry.dndId === targetScope.entries[i].dndId);
      if (unchanged) { setDragPreview(null); return; }

      const saves = finalOrder.map((entry, index) => {
         const changes = { position: index };
         if (entry.dndId === activeId) changes.container = finalPreview.overContainerId;
         if (entry.kind === BLOCK_KIND_CONTAINER) {
            return api.updateContainer(entry.block.id, buildContainerPayload(exerciseId, entry.block, changes));
         }
         const payload = entry.kind === BLOCK_KIND_STATIC
            ? buildElementPayload(exerciseId, entry.block, changes)
            : buildInteractiveElementPayload(exerciseId, entry.block, changes);
         return entry.kind === BLOCK_KIND_STATIC
            ? api.updateElement(entry.block.id, payload)
            : api.updateInteractiveElement(entry.block.id, payload);
      });

      setAwaitingCommit(true);
      Promise.all(saves).then(() => onReload()).catch((err) => {
         console.error(err);
         setAwaitingCommit(false);
         setDragPreview(null);
      });
   };

   const handleDragCancel = () => {
      clearTimeout(dragOverDebounceRef.current);
      clearTimeout(returnToOriginDebounceRef.current);
      setDragPreview(null);
   };

   return (
      <DndContext sensors={sensors} collisionDetection={collisionDetectionRef.current}
         onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
         <SortableContext items={renderedTopLevel.map((entry) => entry.dndId)} strategy={verticalListSortingStrategy}>
            <div className='exercise-block-list'>
               {renderedTopLevel.map((entry, index) => (
                  entry.isDropPlaceholder ? (
                     <div key='drop-placeholder' className='exercise-block-drop-placeholder' />
                  ) : entry.kind === BLOCK_KIND_CONTAINER ? (
                     <ContainerCard key={'container-' + entry.block.id} exerciseId={exerciseId} courseId={courseId} container={entry.block}
                        allElements={elements} allInteractiveElements={interactiveElements} dragPreview={dragPreview}
                        canMoveUp={index > 0} canMoveDown={index < renderedTopLevel.length - 1}
                        onMove={(direction) => handleMove(index, direction)}
                        onDeleted={onReload} onReload={onReload} />
                  ) : (
                     <BlockCard key={entry.kind + '-' + entry.block.id} exerciseId={exerciseId} courseId={courseId} entry={entry}
                        canMoveUp={index > 0} canMoveDown={index < renderedTopLevel.length - 1}
                        onMove={(direction) => handleMove(index, direction)}
                        onDeleted={onReload} onSaved={onReload} />
                  )
               ))}
               <BlockTypePicker isOpen={pickerOpen} onToggle={() => setPickerOpen(prev => !prev)} onPick={handleAddBlock} onPickContainer={handleAddContainer} dropRegionId={TOP_LEVEL_END_ID} />
            </div>
         </SortableContext>
         <DragOverlay>
            {dragPreview && dragPreview.activeEntry ? (
               <div className='exercise-block-card exercise-block-card-overlay'>
                  <div className='exercise-block-card-toolbar'>
                     <span className='exercise-block-card-type-label'>
                        {dragPreview.activeEntry.kind === BLOCK_KIND_CONTAINER
                           ? 'Контейнер'
                           : (getBlockType(dragPreview.activeEntry.block.type) || { label: dragPreview.activeEntry.block.type }).label}
                     </span>
                  </div>
               </div>
            ) : null}
         </DragOverlay>
      </DndContext>
   );
}
